import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import TopBar from './../../../layout/TopBar';
import FolderGrid from '../components/FolderGrid';
import PlusCircle from '../../../macadamia-core/icons/PlusCircle';
import CreateFolderModal from '../components/CreateFolderModal';
import EditFolderModal from '../components/EditFolderModal';
import ImportFolderModal from '../components/ImportFolderModal';
import FilterTabs from '../components/FilterTabs';
import RessourcesSidebar from '../components/RessourcesSidebar';
import StatistiquesSidebar from '../components/StatistiquesSidebar';
import useViewport from '../../../hooks/useViewport';

interface IDossier {
  _id: string;
  title: string;
  description: string;
  createdAt: string;
  updatedAt: string;
  settings: {
    visibility: string;
  };
  author: string;
  coAuthors: string[];
}

const MyFolders = () => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [selectedDossier, setSelectedDossier] = useState<IDossier | null>(null); 
  const [isImportModalOpen, setIsImportModalOpen] = useState<boolean>(false);
  const [dossiersUpdated, setDossiersUpdated] = useState<boolean>(false);
  const [selectedFilter, setSelectedFilter] = useState<string>('Tous');
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const [invitationCount, setInvitationCount] = useState<number>(0);
  const [grantedCount, setGrantedCount] = useState<number>(0);
  const { isMobile } = useViewport();
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Fermer le dropdown quand on clique ailleurs
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleNewFolder = () => {
    setIsDropdownOpen(false);
    setIsModalOpen(true);
  };

  const handleImportation = () => {
    setIsDropdownOpen(false);
    setIsImportModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  // ← AJOUTER CETTE FONCTION
  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setSelectedDossier(null);
  };

  const closeImportModal = () => {
    setIsImportModalOpen(false);
  };

  const handleFolderCreated = (newFolder: any) => {
    setDossiersUpdated(true);
    setIsModalOpen(false);
  };

  // ← AJOUTER CETTE FONCTION
  const handleFolderUpdated = (updatedFolder: IDossier) => {
    setDossiersUpdated(true);
    setIsEditModalOpen(false);
    setSelectedDossier(null);
  };

  const handleFolderImported = (importedFolder: any) => {
    setDossiersUpdated(true);
    setIsImportModalOpen(false);
  };

  const handleFilterChange = (filter: string) => {
    setSelectedFilter(filter);
  };

  // ← AJOUTER CETTE FONCTION
  const handleEditDossier = (dossier: IDossier) => {
    setSelectedDossier(dossier);
    setIsEditModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-mcdm-primary-100 dark:bg-mcdm-third-500">
      <TopBar />

      <div className="pt-24 pb-24 px-4 sm:px-6 lg:px-8">
        <div className={`max-w-7xl mx-auto ${isMobile ? 'flex flex-col gap-6' : 'flex gap-6 items-start'}`}>
          {/* Main content */}
          <div className="w-full flex-1">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
              <div className="flex items-center min-h-[40px]">
                <h1 className="text-xl font-platypi text-mcdm-third-500 dark:text-mcdm-primary-100 leading-none">
                  Mes Dossiers
                </h1>

                {/* Dropdown Menu Container */}
                <div className="relative ml-4" ref={dropdownRef}>
                  <button
                    onClick={toggleDropdown}
                    className="flex items-center justify-center w-8 h-8 text-mcdm-third-500 dark:text-mcdm-primary-100 hover:text-mcdm-secondary-400 dark:hover:text-mcdm-secondary-300 transition-colors"
                  >
                    <motion.div
                      animate={{ rotate: isDropdownOpen ? 45 : 0 }}
                      transition={{ duration: 0.2 }}
                      className="flex items-center justify-center"
                    >
                      <PlusCircle width="24px" height="24px" fill="currentColor" />
                    </motion.div>
                  </button>

                  {/* Dropdown Menu */}
                  <AnimatePresence>
                    {isDropdownOpen && (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: -10 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: -10 }}
                        transition={{ duration: 0.2 }}
                        className="absolute top-full left-0 mt-2 w-48 bg-white dark:bg-mcdm-third-400 rounded-lg shadow-lg border border-gray-200 dark:border-mcdm-third-300 overflow-hidden z-50"
                      >
                        <div className="py-1">
                          <motion.button
                            whileHover={{ backgroundColor: "rgba(0,0,0,0.05)" }}
                            whileTap={{ scale: 0.98 }}
                            onClick={handleNewFolder}
                            className="w-full px-4 py-2 text-left text-sm text-mcdm-third-500 dark:text-mcdm-primary-100 hover:bg-gray-50 dark:hover:bg-mcdm-third-300 transition-colors"
                          >
                            Nouveau dossier
                          </motion.button>

                          <motion.button
                            whileHover={{ backgroundColor: "rgba(0,0,0,0.05)" }}
                            whileTap={{ scale: 0.98 }}
                            onClick={handleImportation}
                            className="w-full px-4 py-2 text-left text-sm text-mcdm-third-500 dark:text-mcdm-primary-100 hover:bg-gray-50 dark:hover:bg-mcdm-third-300 transition-colors"
                          >
                            Importation
                          </motion.button>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>

              <FilterTabs
                selectedFilter={selectedFilter}
                onFilterChange={handleFilterChange}
                invitationCount={invitationCount}
                grantedCount={grantedCount}
              />
            </div>

            <FolderGrid
              dossiersUpdated={dossiersUpdated}
              setDossiersUpdated={setDossiersUpdated}
              selectedFilter={selectedFilter}
              onEditDossier={handleEditDossier}
              onInvitationCountChange={setInvitationCount}
              onGrantedCountChange={setGrantedCount}
            />
          </div>

          {/* Right Sidebar */}
          <div className="space-y-6">
            <StatistiquesSidebar />
            <RessourcesSidebar />
          </div>
        </div>
      </div>

      <CreateFolderModal
        isOpen={isModalOpen}
        onClose={closeModal}
        onFolderCreated={handleFolderCreated}
      />

      <EditFolderModal
        isOpen={isEditModalOpen}
        onClose={closeEditModal}
        onFolderUpdated={handleFolderUpdated}
        dossier={selectedDossier}
      />

      <ImportFolderModal
        isOpen={isImportModalOpen}
        onClose={closeImportModal}
        onFolderImported={handleFolderImported}
      />
    </div>
  );
};

export default MyFolders;